import { captureConversationAccess } from 'dashboard/permissionSets/conversationReads';
import types from '../mutation-types';
import { throwErrorMessage } from 'dashboard/store/utils/api';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import ConversationInboxApi from '../../api/inbox/conversation';

const FILTERED_UNREAD_COUNTS_REFRESH_RETRY_MS = 30000;
const FILTERED_UNREAD_COUNTS_REFRESH_RETRY_JITTER_MS = 15000;
const getFilteredUnreadCountsRefreshRetryDelay = () =>
  FILTERED_UNREAD_COUNTS_REFRESH_RETRY_MS +
  Math.random() * FILTERED_UNREAD_COUNTS_REFRESH_RETRY_JITTER_MS;

const state = {
  records: {},
  uiFlags: {
    isFetching: false,
    isUpdating: false,
  },
};

export const getters = {
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getByConversationId: _state => conversationId => {
    return _state.records[conversationId];
  },
};

const hasFeatureEnabled = (rootGetters, featureFlag) => {
  const accountId = rootGetters?.getCurrentAccountId;
  const isFeatureEnabled = rootGetters?.['accounts/isFeatureEnabledonAccount'];

  return Boolean(accountId && isFeatureEnabled?.(accountId, featureFlag));
};

const hasCurrentUser = (participants, currentUserId) =>
  (Array.isArray(participants) ? participants : []).some(
    participant => participant.id === currentUserId
  );

const refreshConversationUnreadCounts = dispatch => {
  dispatch('conversationUnreadCounts/get', {}, { root: true });
  setTimeout(
    () => dispatch('conversationUnreadCounts/get', {}, { root: true }),
    getFilteredUnreadCountsRefreshRetryDelay()
  );
};

const shouldRefreshConversationUnreadCounts = (
  { rootGetters, state: moduleState },
  conversationId,
  participants
) => {
  const currentUserId =
    rootGetters?.getCurrentUserID || rootGetters?.getCurrentUser?.id;

  return (
    currentUserId &&
    hasFeatureEnabled(rootGetters, FEATURE_FLAGS.CONVERSATION_UNREAD_COUNTS) &&
    hasFeatureEnabled(rootGetters, FEATURE_FLAGS.UNREAD_COUNT_FOR_FILTERS) &&
    hasCurrentUser(moduleState.records[conversationId], currentUserId) !==
      hasCurrentUser(participants, currentUserId)
  );
};

const participantReads = new Map();

// Only the newest participants read or write of a conversation applies its
// response.
const startParticipantsRead = conversationId => {
  const read = {};
  participantReads.set(conversationId, read);
  return () => participantReads.get(conversationId) === read;
};

export const actions = {
  show: async (context, { conversationId }) => {
    const { commit } = context;
    const accessIsCurrent = captureConversationAccess(context, conversationId);
    // Unlike the response, the loading state survives a revocation of this
    // conversation only: an account-wide reset already cleared it.
    const accountIsCurrent = captureConversationAccess(context);
    const isLatest = startParticipantsRead(conversationId);
    const isCurrent = () => accessIsCurrent() && isLatest();
    commit(types.SET_CONVERSATION_PARTICIPANTS_UI_FLAG, {
      isFetching: true,
    });

    try {
      const response =
        await ConversationInboxApi.fetchParticipants(conversationId);
      if (!isCurrent()) return;
      commit(types.SET_CONVERSATION_PARTICIPANTS, {
        conversationId,
        data: response.data,
      });
      commit(
        types.SET_CONVERSATION_PARTICIPANTS,
        { conversationId, data: response.data },
        { root: true }
      );
    } catch (error) {
      if (isCurrent()) throwErrorMessage(error);
    } finally {
      // The newest read ends the loading state even when a revocation of
      // this conversation fenced its response off.
      if (accountIsCurrent() && isLatest()) {
        commit(types.SET_CONVERSATION_PARTICIPANTS_UI_FLAG, {
          isFetching: false,
        });
      }
    }
  },

  update: async (context, { conversationId, userIds }) => {
    const { commit, dispatch, rootGetters, state: moduleState } = context;
    const isCurrent = captureConversationAccess(context, conversationId);
    const accountIsCurrent = captureConversationAccess(context);
    // Writes join the read ordering: a newer read or write wins.
    const isLatest = startParticipantsRead(conversationId);
    commit(types.SET_CONVERSATION_PARTICIPANTS_UI_FLAG, {
      isUpdating: true,
    });

    try {
      const response = await ConversationInboxApi.updateParticipants({
        conversationId,
        userIds,
      });
      if (!isCurrent() || !isLatest()) return;
      const shouldRefreshUnreadCounts = shouldRefreshConversationUnreadCounts(
        { rootGetters, state: moduleState },
        conversationId,
        response.data
      );
      commit(types.SET_CONVERSATION_PARTICIPANTS, {
        conversationId,
        data: response.data,
      });
      commit(
        types.SET_CONVERSATION_PARTICIPANTS,
        { conversationId, data: response.data },
        { root: true }
      );
      if (shouldRefreshUnreadCounts) {
        refreshConversationUnreadCounts(dispatch);
      }
    } catch (error) {
      if (isCurrent()) throwErrorMessage(error);
    } finally {
      if (accountIsCurrent() && isLatest()) {
        commit(types.SET_CONVERSATION_PARTICIPANTS_UI_FLAG, {
          isUpdating: false,
        });
      }
    }
  },
};

export const mutations = {
  [types.CLEAR_CONVERSATION_ACCESS]($state) {
    $state.records = {};
    $state.uiFlags = { isFetching: false, isUpdating: false };
  },
  [types.REVOKE_CONVERSATION_ACCESS]($state, conversationId) {
    const { [conversationId]: _revoked, ...records } = $state.records;
    $state.records = records;
  },
  [types.SET_CONVERSATION_PARTICIPANTS_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },

  [types.SET_CONVERSATION_PARTICIPANTS]($state, { data, conversationId }) {
    $state.records = {
      ...$state.records,
      [conversationId]: data,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
